<template>
  <div>
    <div v-if="loading" class="sw-wrap">
      <div class="sw-state">
        <b-spinner variant="primary" class="mb-2" />
        <p class="text-muted mb-0">Loading withdrawal detail...</p>
      </div>
    </div>

    <template v-else-if="detail">
      <!-- Professional overview -->
      <div class="sw-wrap mb-3">
        <div class="gradient-line-wrapper"></div>
        <b-card class="sw-inner-card border-0">
          <div
            class="d-flex justify-content-between align-items-center mb-2 flex-wrap"
            style="gap: 0.65rem"
          >
            <div class="d-flex align-items-center flex-wrap" style="gap: 0.65rem">
              <b-button
                v-if="embedded"
                variant="outline-secondary"
                size="sm"
                class="sw-back-btn"
                v-b-tooltip.hover.top="'Back to Inbox'"
                @click="onBack"
              >
                <feather-icon icon="ArrowLeftIcon" size="16" />
              </b-button>
              <div>
                <h5 class="mb-0 resource-title">
                  Withdrawal #{{ header.id }}
                </h5>
                <small class="text-muted">{{
                  currentStepText(header.status)
                }}</small>
              </div>
            </div>
            <div class="d-flex flex-wrap align-items-center" style="gap: 0.45rem">
              <span
                class="status-pill"
                :class="'status-pill--' + (header.status || 'draft')"
              >
                {{ statusLabel(header.status) }}
              </span>
              <b-button
                v-if="effectivePermissions.canEditForm"
                size="sm"
                variant="primary"
                class="action-btn-add"
                @click="onEdit"
              >
                <feather-icon icon="EditIcon" size="14" class="mr-25" />
                {{ isParentInitiatedWithdrawal(detail) ? "Review Form" : "Edit Form" }}
              </b-button>
            </div>
          </div>

          <!-- Student identity card -->
          <div class="sw-profile-banner mb-2">
            <div class="d-flex align-items-center flex-wrap" style="gap: 1rem">
              <b-avatar
                :src="student.picture || ''"
                :text="actorInitials(student.name || 'S')"
                size="64"
                variant="light-primary"
                class="sw-profile-avatar flex-shrink-0"
              />
              <div class="flex-grow-1 min-width-0">
                <h4 class="sw-profile-name mb-25">
                  {{ student.name || "—" }}
                </h4>
                <p
                  v-if="student.father_name"
                  class="sw-profile-fn mb-50"
                >
                  F/N: {{ student.father_name }}
                </p>
                <div
                  class="d-flex flex-wrap align-items-center mb-50"
                  style="gap: 0.4rem"
                >
                  <b-badge variant="light-primary">
                    GR {{ student.grNo || "—" }}
                  </b-badge>
                  <b-badge variant="light-info">
                    {{ student.className || "—"
                    }}{{
                      student.sectionName
                        ? " - " + student.sectionName
                        : ""
                    }}
                  </b-badge>
                  <b-badge
                    v-if="header.leavingCategory"
                    variant="light-warning"
                  >
                    {{ header.leavingCategory }}
                  </b-badge>
                </div>
                <div
                  class="d-flex flex-wrap"
                  style="gap: 0.5rem"
                >
                  <div
                    v-if="header.parentContact"
                    class="sw-info-pill"
                  >
                    <feather-icon
                      icon="PhoneIcon"
                      size="14"
                      class="mr-50 text-primary"
                    />
                    {{ header.parentContact }}
                  </div>
                  <div
                    v-if="header.forwardingSchool"
                    class="sw-info-pill"
                  >
                    <feather-icon
                      icon="HomeIcon"
                      size="14"
                      class="mr-50 text-primary"
                    />
                    {{ header.forwardingSchool }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Details grid -->
          <div class="sw-detail-grid">
            <div class="sw-detail-cell">
              <span class="sw-detail-label">Request date</span>
              <span class="sw-detail-value">{{
                formatDateTime(
                  header.requestDate ||
                    header.initiatedOn ||
                    header.createdOn
                )
              }}</span>
            </div>
            <div class="sw-detail-cell">
              <span class="sw-detail-label">Last attendance</span>
              <span class="sw-detail-value">{{
                formatDate(header.lastAttendanceDate)
              }}</span>
            </div>
            <div class="sw-detail-cell">
              <span class="sw-detail-label">TC required</span>
              <span class="sw-detail-value">{{
                header.tcRequired ? "Yes" : "No"
              }}</span>
            </div>
            <div class="sw-detail-cell">
              <span class="sw-detail-label">Parent contact</span>
              <span class="sw-detail-value">{{
                header.parentContact || "—"
              }}</span>
            </div>
            <div class="sw-detail-cell sw-detail-cell--span2">
              <span class="sw-detail-label">Forwarding school</span>
              <span class="sw-detail-value">{{
                header.forwardingSchool || "—"
              }}</span>
            </div>
            <div class="sw-detail-cell sw-detail-cell--full">
              <span class="sw-detail-label">Reason</span>
              <span class="sw-detail-value">{{ header.reason || "—" }}</span>
            </div>
            <div
              v-if="header.remarks"
              class="sw-detail-cell sw-detail-cell--full"
            >
              <span class="sw-detail-label">Remarks</span>
              <span class="sw-detail-value">{{ header.remarks }}</span>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Available actions -->
      <div class="sw-wrap mb-3">
        <div class="gradient-line-wrapper"></div>
        <b-card class="sw-inner-card border-0">
          <div class="mb-2">
            <h5 class="mb-0 resource-title">Available Actions</h5>
            <small class="text-muted"
              >Shown based on your role and request status</small
            >
          </div>

          <div v-if="!hasAnyAction" class="sw-empty-actions">
            <feather-icon icon="SlashIcon" size="22" class="text-muted mb-50" />
            <p class="text-muted small mb-0">
              {{ emptyActionsMessage }}
            </p>
          </div>

          <div
            v-else
            class="sw-action-grid"
            :class="{ 'sw-action-grid--single': isSingleActionView }"
          >
            <!-- Submit -->
            <div
              v-if="effectivePermissions.canSubmit"
              class="sw-action-panel sw-action-panel--submit"
            >
              <div class="sw-action-panel__head">
                <div class="sw-action-icon sw-action-icon--primary">
                  <feather-icon icon="SendIcon" size="18" />
                </div>
                <div class="flex-grow-1">
                  <div class="sw-action-panel__title">
                    {{
                      header.status === "pending_admission"
                        ? "Ready to resubmit"
                        : "Proceed request"
                    }}
                  </div>
                  <div class="sw-action-panel__sub">
                    {{
                      header.status === "pending_admission"
                        ? "Updates are done — send this request back to Branch Head"
                        : "Send this draft to Branch Head for review"
                    }}
                  </div>
                </div>
              </div>
              <b-button
                variant="primary"
                class="sw-action-btn sw-action-btn--primary"
                :disabled="acting"
                @click="doSubmit"
              >
                <b-spinner v-if="acting" small class="mr-50" />
                <feather-icon v-else icon="SendIcon" size="15" class="mr-50" />
                Proceed to Branch Head
              </b-button>
            </div>

            <!-- Add action / retention -->
            <div v-if="effectivePermissions.canAddAction" class="sw-action-panel">
              <div class="sw-action-panel__head">
                <div class="sw-action-icon sw-action-icon--primary">
                  <feather-icon icon="MessageSquareIcon" size="16" />
                </div>
                <div>
                  <div class="sw-action-panel__title">Add action / note</div>
                  <div class="sw-action-panel__sub">
                    Log a retention attempt or follow-up
                  </div>
                </div>
              </div>
              <v-select
                v-model="actionForm.actionType"
                :options="actionTypes"
                :reduce="(o) => o.value"
                label="text"
                class="mb-1"
                append-to-body
                placeholder="Select action type"
              />
              <b-form-textarea
                v-model="actionForm.note"
                rows="2"
                class="sw-action-input mb-1"
                placeholder="Called father, offered fee discount…"
              />
              <b-form-group label="Attachment" class="mb-1 sw-action-attachment">
                <div class="sw-file-picker">
                  <div class="sw-file-picker__field">
                    <span
                      class="sw-file-picker__name"
                      :class="{ 'text-muted': !actionAttachmentDisplayName }"
                      :title="
                        actionAttachmentDisplayName ||
                          'Choose file or drop it here...'
                      "
                    >
                      {{
                        actionAttachmentDisplayName ||
                          "Choose file or drop it here..."
                      }}
                    </span>
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      class="sw-file-picker__btn"
                      :disabled="acting || actionAttachmentUploading"
                      @click="$refs.actionAttachmentInput.click()"
                    >
                      Browse
                    </b-button>
                  </div>
                  <input
                    ref="actionAttachmentInput"
                    type="file"
                    class="d-none"
                    accept=".pdf,.doc,.jpg,.jpeg,.png,application/pdf,application/msword,image/jpeg,image/png"
                    @change="handleActionAttachmentChange"
                  />
                </div>
                <small class="text-muted d-block mt-25">
                  PNG, JPG, DOC, PDF only · Max 25 MB
                </small>
                <div
                  v-if="actionForm.attachment"
                  class="sw-action-attachment-preview mt-50"
                >
                  <feather-icon icon="PaperclipIcon" size="14" class="mr-25" />
                  <a
                    :href="getAttachmentPreviewUrl(actionForm.attachment)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="sw-action-attachment-link"
                  >
                    {{ getFileName(actionForm.attachment) }}
                  </a>
                  <button
                    type="button"
                    class="sw-action-attachment-clear"
                    :disabled="acting || actionAttachmentUploading"
                    @click="clearActionAttachment"
                  >
                    <feather-icon icon="XIcon" size="13" />
                  </button>
                </div>
                <div
                  v-if="actionAttachmentUploading"
                  class="text-primary small mt-25"
                >
                  <b-spinner small class="mr-50" />
                  Uploading attachment...
                </div>
              </b-form-group>
              <b-button
                variant="primary"
                class="sw-action-btn sw-action-btn--primary"
                :disabled="acting || !actionForm.note || actionAttachmentUploading"
                @click="doAddAction"
              >
                <feather-icon icon="PlusIcon" size="14" class="mr-50" />
                Add Action
              </b-button>
            </div>

            <!-- Reassign -->
            <div v-if="effectivePermissions.canReassign" class="sw-action-panel">
              <div class="sw-action-panel__head">
                <div class="sw-action-icon sw-action-icon--info">
                  <feather-icon icon="ShuffleIcon" size="16" />
                </div>
                <div>
                  <div class="sw-action-panel__title">Reassign</div>
                  <div class="sw-action-panel__sub">
                    Move this request to another role
                  </div>
                </div>
              </div>
              <b-form-textarea
                v-model="reassignNote"
                rows="2"
                class="sw-action-input mb-1"
                placeholder="Need TC docs / Cleared for leave…"
              />
              <div class="d-flex flex-wrap" style="gap: 0.5rem">
                <b-button
                  variant="outline-info"
                  class="sw-action-btn-sm"
                  :disabled="acting"
                  @click="doReassign('admission_officer')"
                >
                  <feather-icon icon="UserIcon" size="14" class="mr-50" />
                  Admission
                </b-button>
                <b-button
                  variant="outline-primary"
                  class="sw-action-btn-sm"
                  :disabled="acting"
                  @click="doReassign('finance_officer')"
                >
                  <feather-icon icon="DollarSignIcon" size="14" class="mr-50" />
                  Finance
                </b-button>
              </div>
            </div>

            <!-- Hold / Reject (pending Branch Head only) -->
            <div
              v-if="effectivePermissions.canHoldReject"
              class="sw-action-panel"
            >
              <div class="sw-action-panel__head">
                <div class="sw-action-icon sw-action-icon--warning">
                  <feather-icon icon="AlertTriangleIcon" size="16" />
                </div>
                <div>
                  <div class="sw-action-panel__title">Status decision</div>
                  <div class="sw-action-panel__sub">
                    Put on hold or reject this request
                  </div>
                </div>
              </div>
              <b-form-textarea
                v-model="statusNote"
                rows="2"
                class="sw-action-input mb-1"
                placeholder="Add a status note…"
              />
              <div class="sw-status-btns sw-status-btns--two">
                <b-button
                  class="sw-status-btn sw-status-btn--hold"
                  :disabled="acting"
                  @click="doSetStatus('on_hold')"
                >
                  <feather-icon icon="PauseCircleIcon" size="14" class="mr-50" />
                  Hold
                </b-button>
                <b-button
                  class="sw-status-btn sw-status-btn--reject"
                  :disabled="acting"
                  @click="doSetStatus('rejected')"
                >
                  <feather-icon icon="XCircleIcon" size="14" class="mr-50" />
                  Reject
                </b-button>
              </div>
            </div>

            <!-- Resume (on hold — Branch Head only) -->
            <div
              v-if="effectivePermissions.canResume"
              class="sw-action-panel"
            >
              <div class="sw-action-panel__head">
                <div class="sw-action-icon sw-action-icon--success">
                  <feather-icon icon="PlayCircleIcon" size="16" />
                </div>
                <div>
                  <div class="sw-action-panel__title">Resume request</div>
                  <div class="sw-action-panel__sub">
                    This request is on hold — only Branch Head can resume it
                  </div>
                </div>
              </div>
              <b-form-textarea
                v-model="statusNote"
                rows="2"
                class="sw-action-input mb-1"
                placeholder="Optional resume note…"
              />
              <b-button
                class="sw-action-btn"
                variant="success"
                :disabled="acting"
                @click="doSetStatus('resume')"
              >
                <feather-icon icon="PlayCircleIcon" size="14" class="mr-50" />
                Resume
              </b-button>
            </div>

            <!-- Fee clearance (Finance — left) -->
            <div
              v-if="effectivePermissions.showFeeClearance"
              class="sw-action-panel"
            >
              <div class="sw-action-panel__head">
                <div class="sw-action-icon sw-action-icon--info">
                  <feather-icon icon="CreditCardIcon" size="16" />
                </div>
                <div class="flex-grow-1 min-width-0">
                  <div class="sw-action-panel__title">Fee clearance</div>
                  <div class="sw-action-panel__sub">
                    Confirm dues are cleared before marking left
                  </div>
                </div>
                <b-button
                  v-if="canViewFeeHistory"
                  variant="outline-primary"
                  size="sm"
                  class="sw-fee-history-btn flex-shrink-0"
                  :disabled="feeHistoryLoading"
                  @click="openFeeHistoryModal"
                >
                  <b-spinner v-if="feeHistoryLoading" small class="mr-25" />
                  <feather-icon
                    v-else
                    icon="ClockIcon"
                    size="14"
                    class="mr-25"
                  />
                  View Fees History
                </b-button>
              </div>
              <b-form-textarea
                v-model="feeClearanceNote"
                rows="2"
                class="sw-action-input mb-1"
                placeholder="All dues cleared / challan settled"
              />
              <b-form-group label="Attachment" class="mb-1 sw-action-attachment">
                <div class="sw-file-picker">
                  <div class="sw-file-picker__field">
                    <span
                      class="sw-file-picker__name"
                      :class="{ 'text-muted': !feeClearanceAttachmentDisplayName }"
                      :title="
                        feeClearanceAttachmentDisplayName ||
                          'Choose file or drop it here...'
                      "
                    >
                      {{
                        feeClearanceAttachmentDisplayName ||
                          "Choose file or drop it here..."
                      }}
                    </span>
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      class="sw-file-picker__btn"
                      :disabled="acting || feeClearanceAttachmentUploading"
                      @click="$refs.feeClearanceAttachmentInput.click()"
                    >
                      Browse
                    </b-button>
                  </div>
                  <input
                    ref="feeClearanceAttachmentInput"
                    type="file"
                    class="d-none"
                    accept=".pdf,.doc,.jpg,.jpeg,.png,application/pdf,application/msword,image/jpeg,image/png"
                    @change="handleFeeClearanceAttachmentChange"
                  />
                </div>
                <small class="text-muted d-block mt-25">
                  PNG, JPG, DOC, PDF only · Max 25 MB
                </small>
                <div
                  v-if="feeClearanceAttachment"
                  class="sw-action-attachment-preview mt-50"
                >
                  <feather-icon icon="PaperclipIcon" size="14" class="mr-25" />
                  <a
                    :href="getAttachmentPreviewUrl(feeClearanceAttachment)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="sw-action-attachment-link"
                  >
                    {{ getFileName(feeClearanceAttachment) }}
                  </a>
                  <button
                    type="button"
                    class="sw-action-attachment-clear"
                    :disabled="acting || feeClearanceAttachmentUploading"
                    @click="clearFeeClearanceAttachment"
                  >
                    <feather-icon icon="XIcon" size="13" />
                  </button>
                </div>
                <div
                  v-if="feeClearanceAttachmentUploading"
                  class="text-primary small mt-25"
                >
                  <b-spinner small class="mr-50" />
                  Uploading attachment...
                </div>
              </b-form-group>
              <b-button
                variant="primary"
                class="sw-action-btn sw-action-btn--primary"
                :disabled="
                  acting ||
                  !feeClearanceNoteTrimmed ||
                  feeClearanceAttachmentUploading
                "
                @click="doSaveFeeClearance"
              >
                <feather-icon icon="SaveIcon" size="14" class="mr-50" />
                Save Remarks
              </b-button>
            </div>

            <!-- Proceed to Head Office (Finance — campus 10+) -->
            <div
              v-if="effectivePermissions.canProceedToHeadOffice"
              class="sw-action-panel sw-action-panel--submit"
            >
              <div class="sw-action-panel__head">
                <div class="sw-action-icon sw-action-icon--primary">
                  <feather-icon icon="SendIcon" size="16" />
                </div>
                <div>
                  <div class="sw-action-panel__title">Proceed to Head Office</div>
                  <div class="sw-action-panel__sub">
                    Send to Head Office for final processing (campus 10+)
                  </div>
                </div>
              </div>
              <b-button
                variant="primary"
                class="sw-action-btn sw-action-btn--primary"
                :disabled="acting || !feeClearanceNoteTrimmed"
                @click="confirmProceedHeadOffice = true"
              >
                <feather-icon icon="SendIcon" size="14" class="mr-50" />
                Proceed to Head Office
              </b-button>
            </div>

            <!-- Mark student left (Finance campus ≤9, or Head Office) -->
            <div
              v-if="effectivePermissions.canFinalizeLeft"
              class="sw-action-panel sw-action-panel--danger"
            >
              <div class="sw-action-panel__head">
                <div class="sw-action-icon sw-action-icon--danger">
                  <feather-icon icon="UserMinusIcon" size="16" />
                </div>
                <div>
                  <div class="sw-action-panel__title">Mark student left</div>
                  <div class="sw-action-panel__sub">
                    Final step — cannot be undone
                  </div>
                </div>
              </div>
              <b-button
                variant="success"
                class="sw-action-btn"
                :disabled="acting || !feeClearanceNoteTrimmed"
                @click="confirmFinalize = true"
              >
                <feather-icon icon="CheckCircleIcon" size="14" class="mr-50" />
                Mark Student Left
              </b-button>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Activity / History -->
      <div class="sw-wrap">
        <div class="gradient-line-wrapper"></div>
        <b-card class="sw-inner-card border-0">
          <b-tabs pills content-class="mt-1">
            <b-tab title="Action History" active>
              <div
                v-if="!normalizedActionHistory.length"
                class="text-muted py-2"
              >
                No action history yet.
              </div>
              <div v-else class="sw-history-list">
                <div
                  v-for="(a, idx) in normalizedActionHistory"
                  :key="'a-' + idx"
                  class="sw-history-item"
                >
                  <div class="sw-history-avatar">
                    <b-avatar
                      :text="actorInitials(a.actorName)"
                      size="40"
                      variant="light-primary"
                    />
                  </div>
                  <div class="sw-history-body">
                    <div
                      class="d-flex justify-content-between align-items-start flex-wrap"
                      style="gap: 0.35rem"
                    >
                      <div>
                        <div class="sw-history-title">
                          {{ a.actionLabel }}
                          <b-badge
                            v-if="a.decision"
                            variant="light-info"
                            class="ml-50"
                          >
                            {{ a.decision }}
                          </b-badge>
                        </div>
                        <div class="sw-history-actor">
                          <feather-icon
                            icon="UserIcon"
                            size="12"
                            class="mr-25"
                          />
                          Done by
                          <strong class="ml-25">{{ a.actorName }}</strong>
                          <span v-if="a.actorRole" class="sw-history-role"
                            >· {{ a.actorRole }}</span
                          >
                        </div>
                      </div>
                      <div class="sw-history-when">
                        <div class="sw-history-when__icon">
                          <feather-icon icon="CalendarIcon" size="13" />
                        </div>
                        <div class="sw-history-when__text">
                          <span class="sw-history-when__date">{{
                            a.whenDate
                          }}</span>
                          <span class="sw-history-when__time">{{
                            a.whenTime
                          }}</span>
                        </div>
                      </div>
                    </div>
                    <p v-if="a.note" class="sw-history-note mb-0">
                      {{ a.note }}
                    </p>
                    <div
                      v-if="a.attachments && a.attachments.length"
                      class="sw-history-attachments"
                    >
                      <div
                        v-for="(attachment, attIdx) in a.attachments"
                        :key="'att-' + idx + '-' + attIdx"
                        class="sw-history-attachment-item"
                      >
                        <a
                          v-if="isImageFile(attachment)"
                          :href="getAttachmentPreviewUrl(attachment)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="sw-history-attachment-image-link"
                        >
                          <img
                            :src="getAttachmentPreviewUrl(attachment)"
                            :alt="getFileName(attachment)"
                            class="sw-history-attachment-image"
                          />
                        </a>
                        <a
                          v-else
                          :href="getAttachmentPreviewUrl(attachment)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="sw-history-attachment-file"
                        >
                          <feather-icon
                            icon="PaperclipIcon"
                            size="14"
                            class="mr-50"
                          />
                          {{ getFileName(attachment) }}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-tab>

            <b-tab title="Timeline">
              <div v-if="!normalizedTimeline.length" class="text-muted py-2">
                No timeline events yet.
              </div>
              <div v-else class="sw-history-list">
                <div
                  v-for="(ev, idx) in normalizedTimeline"
                  :key="'t-' + idx"
                  class="sw-history-item"
                >
                  <div class="sw-history-avatar">
                    <div class="sw-timeline-dot" />
                  </div>
                  <div class="sw-history-body">
                    <div
                      class="d-flex justify-content-between align-items-start flex-wrap"
                      style="gap: 0.35rem"
                    >
                      <div>
                        <div class="sw-history-title">{{ ev.actionLabel }}</div>
                        <div class="sw-history-actor">
                          <feather-icon
                            icon="UserIcon"
                            size="12"
                            class="mr-25"
                          />
                          Done by
                          <strong class="ml-25">{{ ev.actorName }}</strong>
                          <span v-if="ev.actorRole" class="sw-history-role"
                            >· {{ ev.actorRole }}</span
                          >
                        </div>
                      </div>
                      <div class="sw-history-when">
                        <div class="sw-history-when__icon">
                          <feather-icon icon="CalendarIcon" size="13" />
                        </div>
                        <div class="sw-history-when__text">
                          <span class="sw-history-when__date">{{
                            ev.whenDate
                          }}</span>
                          <span class="sw-history-when__time">{{
                            ev.whenTime
                          }}</span>
                        </div>
                      </div>
                    </div>
                    <p v-if="ev.note" class="sw-history-note mb-0">
                      {{ ev.note }}
                    </p>
                  </div>
                </div>
              </div>
            </b-tab>
          </b-tabs>
        </b-card>
      </div>
    </template>

    <div v-else class="sw-wrap">
      <div class="sw-state text-muted">Withdrawal not found</div>
    </div>

    <b-modal
      v-model="confirmProceedHeadOffice"
      title="Confirm Proceed to Head Office"
      centered
      ok-title="Yes, proceed"
      ok-variant="primary"
      cancel-title="Cancel"
      @ok="doProceedToHeadOffice"
    >
      <p class="mb-1">
        This will send the withdrawal to <strong>Head Office</strong> for final
        processing.
      </p>
      <p v-if="feeClearanceNoteTrimmed" class="mb-0 small text-muted">
        Fee clearance note:
        <strong>{{ feeClearanceNoteTrimmed }}</strong>
      </p>
    </b-modal>

    <b-modal
      v-model="confirmFinalize"
      title="Confirm Mark Student Left"
      centered
      ok-title="Yes, mark Left"
      ok-variant="danger"
      cancel-title="Cancel"
      @ok="doFinalize"
    >
      <p class="mb-1">
        This will mark the student as <strong>Left</strong>. Cannot undo.
      </p>
      <p v-if="feeClearanceNoteTrimmed" class="mb-0 small text-muted">
        Fee clearance note:
        <strong>{{ feeClearanceNoteTrimmed }}</strong>
      </p>
    </b-modal>

    <b-modal
      v-model="feeHistoryModalVisible"
      title="Fees History"
      size="xl"
      scrollable
      hide-footer
      @hidden="onFeeHistoryModalHidden"
    >
      <div class="sw-fee-history-meta mb-2">
        <div class="d-flex flex-wrap align-items-center" style="gap: 0.5rem">
          <b-badge variant="light-primary">
            {{ student.name || "Student" }}
          </b-badge>
          <b-badge v-if="student.grNo" variant="light-info">
            GR {{ student.grNo }}
          </b-badge>
          <b-badge v-if="feeHistoryDuesTotal != null" variant="light-warning">
            Outstanding: {{ formatFeeAmount(feeHistoryDuesTotal) }}
          </b-badge>
          <b-badge v-if="feeHistoryTotalCount" variant="light-secondary">
            {{ feeHistoryTotalCount }} record(s)
          </b-badge>
        </div>
      </div>

      <div class="sw-fee-history-table-wrap">
        <b-table
          class="sw-fee-history-table mb-0"
          :busy="feeHistoryLoading"
          show-empty
          small
          hover
          :items="feeHistoryItems"
          :fields="feeHistoryFields"
          :per-page="feeHistoryPerPage"
          :current-page="feeHistoryCurrentPage"
        >
        <template #table-busy>
          <div class="text-center py-3">
            <b-spinner variant="primary" />
            <p class="mb-0 mt-1 text-muted small">Loading fees history...</p>
          </div>
        </template>

        <template #empty>
          <p class="text-center text-muted mb-0 py-2">No fee history found</p>
        </template>

        <template #cell(billID)="{ item }">
          <b-badge
            v-if="!item.fees || item.fees.billID <= 0"
            variant="light-danger"
          >
            {{ (item.fees && item.fees.status) || "—" }}
          </b-badge>
          <b-badge v-else variant="light-primary">
            {{ item.fees.billID }}
          </b-badge>
        </template>

        <template #cell(feeType)="{ item }">
          {{ (item.feesType && item.feesType.feeType) || "—" }}
        </template>

        <template #cell(className)="{ item }">
          {{ formatFeeHistoryClass(item) }}
        </template>

        <template #cell(monthYear)="{ item }">
          <b-badge variant="light-primary">
            {{ (item.fees && item.fees.monthYear) || "—" }}
          </b-badge>
        </template>

        <template #cell(discount)="{ item }">
          <b-badge variant="light-primary">
            {{ getFeeHistoryDiscount(item) }}
          </b-badge>
        </template>

        <template #cell(rcvd)="{ item }">
          <b-badge variant="light-primary">
            {{ getFeeHistoryRcvd(item) }}
          </b-badge>
        </template>

        <template #cell(rcvdDate)="{ item }">
          <b-badge variant="light-primary">
            {{ formatFeeHistoryDate(item.fees && item.fees.rcvdDate) }}
          </b-badge>
        </template>
        </b-table>
      </div>

      <div
        v-if="feeHistoryTotalCount > feeHistoryPerPage"
        class="d-flex justify-content-between align-items-center flex-wrap mt-1"
        style="gap: 0.5rem"
      >
        <small class="text-muted mb-0">
          Page {{ feeHistoryCurrentPage }} of
          {{ feeHistoryTotalPages }}
        </small>
        <b-pagination
          v-model="feeHistoryCurrentPage"
          :total-rows="feeHistoryTotalCount"
          :per-page="feeHistoryPerPage"
          align="right"
          size="sm"
          class="mb-0 sw-fee-history-pagination"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BSpinner,
  BFormTextarea,
  BTabs,
  BTab,
  BModal,
  BBadge,
  BAvatar,
  BTable,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions } from "vuex";
import {
  getUploadPath,
  uploadToMySkool,
  getMediaPreviewUrl,
} from "@/utils/gdriveUpload";
import {
  withdrawalMixin,
  isParentInitiatedWithdrawal,
  ACTION_TYPES,
  timelineLabel,
  formatDateTime,
  formatDate,
} from "./withdrawalShared";

const ACTION_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
const ACTION_ATTACHMENT_ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "image/jpeg",
  "image/jpg",
  "image/png",
];
const ACTION_ATTACHMENT_ALLOWED_EXT = [".pdf", ".doc", ".jpg", ".jpeg", ".png"];

export default {
  name: "WithdrawalDetail",
  components: {
    BCard,
    BButton,
    BSpinner,
    BFormTextarea,
    BTabs,
    BTab,
    BModal,
    BBadge,
    BAvatar,
    BTable,
    BPagination,
    vSelect,
  },
  mixins: [withdrawalMixin],
  props: {
    id: { type: [String, Number], default: null },
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      acting: false,
      detail: null,
      reassignNote: "",
      statusNote: "",
      feeClearanceNote: "",
      feeClearanceAttachment: "",
      feeClearanceAttachmentUploading: false,
      confirmFinalize: false,
      confirmProceedHeadOffice: false,
      actionTypes: ACTION_TYPES,
      actionForm: {
        actionType: "retention_attempt",
        decision: "retain",
        note: "",
        attachment: "",
      },
      actionAttachmentUploading: false,
      feeHistoryModalVisible: false,
      feeHistoryLoading: false,
      feeHistoryItems: [],
      feeHistoryDuesTotal: null,
      feeHistoryCurrentPage: 1,
      feeHistoryPerPage: 20,
      feeHistoryMaxRecords: 500,
      feeHistoryFields: [
        { label: "Slip No#", key: "billID" },
        { label: "Fee Type", key: "feeType" },
        { label: "Class", key: "className" },
        { label: "Details", key: "monthYear" },
        { label: "Discount", key: "discount" },
        { label: "Total", key: "rcvd" },
        { label: "Receiving Date", key: "rcvdDate" },
      ],
    };
  },
  computed: {
    withdrawalId() {
      return this.id || this.$route.params.id;
    },
    header() {
      return (this.detail && this.detail.header) || {};
    },
    student() {
      return (this.detail && this.detail.student) || {};
    },
    permissions() {
      return (
        (this.detail && this.detail.permissions) || {
          canEditForm: false,
          canSubmit: false,
          canReassign: false,
          canAddAction: false,
          canSetHoldReject: false,
          canFinalizeLeft: false,
        }
      );
    },
    /**
     * Role-setup–aware permissions:
     * login userID must match Role Setup assignment, then show that role's actions.
     */
    effectivePermissions() {
      const api = this.permissions;
      const status = String((this.header && this.header.status) || "");
      const isAO = this.hasWithdrawalRole("admission_officer");
      const isBH = this.hasWithdrawalRole("branch_head");
      const isFO = this.hasWithdrawalRole("finance_officer");

      // Closed / rejected — no actions for anyone
      if (status === "rejected" || status === "completed") {
        return {
          canEditForm: false,
          canSubmit: false,
          canAddAction: false,
          canReassign: false,
          canHoldReject: false,
          canResume: false,
          canFinalizeLeft: false,
          canProceedToHeadOffice: false,
          showFeeClearance: false,
        };
      }

      // On hold — only Branch Head can Resume; everyone else blocked
      if (status === "on_hold") {
        return {
          canEditForm: false,
          canSubmit: false,
          canAddAction: false,
          canReassign: false,
          canHoldReject: false,
          canResume: isBH,
          canFinalizeLeft: false,
          canProceedToHeadOffice: false,
          showFeeClearance: false,
        };
      }

      // Pending admission — Admission Officer can review parent requests or re-submit
      if (status === "pending_admission") {
        const parentInitiated = isParentInitiatedWithdrawal(this.detail || {});
        return {
          canEditForm:
            isAO && (parentInitiated || api.canEditForm !== false),
          canSubmit: isAO,
          canAddAction: false,
          canReassign: false,
          canHoldReject: false,
          canResume: false,
          canFinalizeLeft: false,
          canProceedToHeadOffice: false,
          showFeeClearance: false,
        };
      }

      const isHO = this.hasWithdrawalRole("head_office");
      const foStatuses = ["pending_finance"];
      const hoFinalizeStatuses = ["pending_head_office"];

      return {
        canEditForm:
          isAO &&
          (api.canEditForm || status === "draft"),
        canSubmit: isAO && (api.canSubmit || status === "draft"),
        canAddAction: isBH && status === "pending_branch_head",
        canReassign: isBH && status === "pending_branch_head",
        canHoldReject: isBH && status === "pending_branch_head",
        canResume: false,
        canProceedToHeadOffice:
          isFO &&
          this.financeRequiresHeadOfficeProceed &&
          (api.canProceedToHeadOffice || foStatuses.includes(status)),
        canFinalizeLeft:
          (isFO &&
            !this.financeRequiresHeadOfficeProceed &&
            (api.canFinalizeLeft || foStatuses.includes(status))) ||
          (isHO &&
            (api.canFinalizeLeft || hoFinalizeStatuses.includes(status))),
        showFeeClearance: isFO && foStatuses.includes(status),
      };
    },
    timeline() {
      return (this.detail && this.detail.timeline) || [];
    },
    actionHistory() {
      return (this.detail && this.detail.actionHistory) || [];
    },
    feeClearanceNoteTrimmed() {
      return String(this.feeClearanceNote || "").trim();
    },
    actionAttachmentDisplayName() {
      if (this.actionForm.attachment) {
        return this.getFileName(this.actionForm.attachment);
      }
      return "";
    },
    canViewFeeHistory() {
      return (
        this.hasWithdrawalRole("finance_officer") &&
        String((this.header && this.header.status) || "") === "pending_finance"
      );
    },
    feeHistoryTotalCount() {
      return Array.isArray(this.feeHistoryItems)
        ? this.feeHistoryItems.length
        : 0;
    },
    feeHistoryTotalPages() {
      if (!this.feeHistoryTotalCount) return 1;
      return Math.ceil(this.feeHistoryTotalCount / this.feeHistoryPerPage);
    },
    feeClearanceAttachmentDisplayName() {
      if (this.feeClearanceAttachment) {
        return this.getFileName(this.feeClearanceAttachment);
      }
      return "";
    },
    hasAnyAction() {
      const p = this.effectivePermissions;
      return (
        p.canSubmit ||
        p.canAddAction ||
        p.canReassign ||
        p.canHoldReject ||
        p.canResume ||
        p.canFinalizeLeft ||
        p.canProceedToHeadOffice ||
        p.showFeeClearance
      );
    },
    emptyActionsMessage() {
      const status = String((this.header && this.header.status) || "");
      if (status === "rejected") {
        return "This request was rejected — no further actions.";
      }
      if (status === "completed") {
        return "This request is completed — no further actions.";
      }
      if (status === "on_hold") {
        return "Request is on hold. Only Branch Head can resume it.";
      }
      return "No actions available for your role on this step.";
    },
    isSingleActionView() {
      const p = this.effectivePermissions;
      const flags = [
        p.canSubmit,
        p.canAddAction,
        p.canReassign,
        p.canHoldReject,
        p.canResume,
        p.showFeeClearance,
        p.canProceedToHeadOffice,
        p.canFinalizeLeft,
      ];
      return flags.filter(Boolean).length === 1;
    },
    normalizedActionHistory() {
      return (this.actionHistory || []).map((a) => this.normalizeHistoryItem(a, "action"));
    },
    normalizedTimeline() {
      return (this.timeline || []).map((ev) =>
        this.normalizeHistoryItem(ev, "timeline")
      );
    },
  },
  watch: {
    withdrawalId: {
      immediate: true,
      handler(val) {
        if (val) this.load();
      },
    },
  },
  methods: {
    ...mapActions({ get: "get", post: "post" }),
    timelineLabel,
    formatDateTime,
    formatDate,
    isParentInitiatedWithdrawal,
    formatRole(role) {
      if (!role) return "—";
      return String(role)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatActionType(t) {
      return String(t || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    pickActorName(row) {
      if (!row) return "System";
      const name =
        row.userName ||
        row.createdByName ||
        row.actorName ||
        row.performedByName ||
        row.performedBy ||
        row.createdByUser ||
        row.user ||
        row.createdBy ||
        "";
      if (name && String(name).trim() && !/^\d+$/.test(String(name).trim())) {
        return String(name).trim();
      }
      const id =
        row.userID ||
        row.createdByUserID ||
        row.actorUserID ||
        row.createdBy ||
        null;
      if (id) return `User #${id}`;
      return "System";
    },
    pickActorRole(row) {
      if (!row) return "";
      const role =
        row.userRole ||
        row.actorRole ||
        row.role ||
        row.createdByRole ||
        row.performedByRole ||
        "";
      return role ? this.formatRole(role) : "";
    },
    normalizeHistoryItem(row, kind) {
      const actionLabel =
        kind === "timeline"
          ? this.timelineLabel(row.eventType || row.type)
          : this.formatActionType(row.actionType || row.type || row.eventType);
      const whenParts = this.formatHistoryWhen(row);
      return {
        ...row,
        actionLabel: actionLabel || "Update",
        actorName: this.pickActorName(row),
        actorRole: this.pickActorRole(row),
        note: row.note || row.remarks || row.message || "",
        decision: row.decision || "",
        attachment: row.attachment || "",
        attachments: this.getAttachmentList(row.attachment),
        whenDate: whenParts.date,
        whenTime: whenParts.time,
        whenLabel: whenParts.label,
      };
    },
    formatHistoryWhen(row) {
      const raw =
        (row &&
          (row.createdOn || row.date || row.updatedOn || row.timestamp)) ||
        "";
      if (!raw) {
        return { date: "—", time: "", label: "—" };
      }
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) {
        return { date: String(raw), time: "", label: String(raw) };
      }
      const date = d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
      const time = d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      });
      return {
        date,
        time,
        label: `${date}, ${time}`,
      };
    },
    actorInitials(name) {
      const parts = String(name || "S")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      if (!parts.length) return "S";
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0][0] + parts[1][0]).toUpperCase();
    },
    openFeeHistoryModal() {
      if (!this.canViewFeeHistory) {
        this.toastErr("Only Finance Officer can view fees history here");
        return;
      }
      this.feeHistoryCurrentPage = 1;
      this.feeHistoryModalVisible = true;
      this.loadFeeHistory();
    },
    onFeeHistoryModalHidden() {
      this.feeHistoryItems = [];
      this.feeHistoryDuesTotal = null;
      this.feeHistoryCurrentPage = 1;
    },
    async loadFeeHistory() {
      if (!this.canViewFeeHistory) return;

      const sID = Number(this.student.id || this.header.sID || 0);
      const grNo = this.student.grNo || this.header.grNo || "";
      const campusID = Number(
        this.header.campusID ||
          this.student.campusID ||
          this.withdrawalCampusID ||
          0
      );

      if (!sID) {
        this.toastErr("Student not found for fee history");
        return;
      }

      this.feeHistoryLoading = true;
      try {
        const res = await this.get({
          url:
            `${this.$store.state.domain}Fees/GetStudentFeesDataExemption?db=` +
            encodeURIComponent(this.withdrawalDb) +
            "&campusID=" +
            campusID +
            "&grNo=" +
            encodeURIComponent(grNo) +
            "&familyCode=0&sID=" +
            sID,
          token: this.withdrawalToken,
        });

        if (res === "No record found" || !Array.isArray(res) || !res.length) {
          this.feeHistoryItems = [];
          this.feeHistoryDuesTotal = 0;
          return;
        }

        let history = Array.isArray(res[0].history) ? res[0].history : [];
        if (history.length > this.feeHistoryMaxRecords) {
          history = history.slice(0, this.feeHistoryMaxRecords);
          this.toastErr(
            `Showing latest ${this.feeHistoryMaxRecords} fee records only`
          );
        }
        this.feeHistoryItems = history;
        this.feeHistoryDuesTotal = Number(res[0].duesTotal) || 0;
        this.feeHistoryCurrentPage = 1;
      } catch (e) {
        console.error(e);
        this.feeHistoryItems = [];
        this.feeHistoryDuesTotal = null;
        this.toastErr("Failed to load fees history");
      } finally {
        this.feeHistoryLoading = false;
      }
    },
    formatFeeHistoryClass(item) {
      const row = item || {};
      const fees = row.fees || {};
      const cls =
        (row.cls && row.cls.name) ||
        row.className ||
        fees.className ||
        "";
      const sec =
        (row.section && row.section.section) ||
        row.sectionName ||
        fees.sectionName ||
        "";
      if (cls && sec) return `${cls} - ${sec}`;
      return cls || sec || "—";
    },
    getFeeHistoryDiscount(item) {
      const fees = item && item.fees;
      if (!fees || fees.discount == null || fees.discount === "") return "—";
      return fees.discount;
    },
    getFeeHistoryRcvd(item) {
      const fees = item && item.fees;
      if (!fees) return "—";
      if (fees.billID === 0) return fees.exempted != null ? fees.exempted : "—";
      return fees.rcvd != null ? fees.rcvd : "—";
    },
    formatFeeHistoryDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    formatFeeAmount(value) {
      const num = Number(value);
      if (Number.isNaN(num)) return "—";
      return num.toLocaleString("en-PK");
    },
    onEdit() {
      if (this.embedded) this.$emit("edit", this.withdrawalId);
      else {
        this.$router.push({
          name: "student-withdrawal-form",
          query: { id: this.withdrawalId },
        });
      }
    },
    onBack() {
      if (this.embedded) this.$emit("back");
      else this.$router.push({ name: "student-withdrawal" });
    },
    async load() {
      this.loading = true;
      try {
        if (!this.roleSetupLoaded) {
          try {
            await this.loadRoleSetup();
          } catch (e) {
            // still try to load detail
          }
        }
        const res = await this.withdrawalGet("LoadSelected", {
          id: this.withdrawalId,
          role: this.roleForCurrentStatus(),
        });
        this.detail = this.extractDetail(res);
        if (this.header.feeClearanceNote) {
          this.feeClearanceNote = this.header.feeClearanceNote;
        }
      } catch (e) {
        console.error(e);
        this.detail = null;
        this.toastErr("Failed to load withdrawal detail");
      } finally {
        this.loading = false;
      }
    },
    roleForCurrentStatus() {
      const status = String((this.header && this.header.status) || "");
      if (
        status === "pending_head_office" &&
        this.hasWithdrawalRole("head_office")
      ) {
        return "head_office";
      }
      if (
        status === "pending_finance" &&
        this.hasWithdrawalRole("finance_officer")
      ) {
        return "finance_officer";
      }
      if (
        (status === "pending_branch_head" || status === "on_hold") &&
        this.hasWithdrawalRole("branch_head")
      ) {
        return "branch_head";
      }
      if (this.hasWithdrawalRole("admission_officer")) return "admission_officer";
      if (this.hasWithdrawalRole("branch_head")) return "branch_head";
      if (this.hasWithdrawalRole("finance_officer")) return "finance_officer";
      return this.withdrawalRole;
    },
    async runAction(fn) {
      this.acting = true;
      try {
        await fn();
        await this.load();
      } catch (e) {
        console.error(e);
        this.toastErr(
          (e && e.response && e.response.data && e.response.data.message) ||
            "Action failed"
        );
      } finally {
        this.acting = false;
      }
    },
    doSubmit() {
      return this.runAction(async () => {
        await this.withdrawalPost(
          "Submit",
          {},
          {
            id: this.withdrawalId,
            userID: this.withdrawalUserID,
            role: "admission_officer",
          }
        );
        this.toastOk("Proceeded to Branch Head");
      });
    },
    doAddAction() {
      return this.runAction(async () => {
        const role = this.hasWithdrawalRole("branch_head")
          ? "branch_head"
          : "admission_officer";
        await this.withdrawalPost(
          "AddAction",
          { ...this.actionForm },
          {
            id: this.withdrawalId,
            userID: this.withdrawalUserID,
            role,
          }
        );
        this.toastOk("Action added");
        this.actionForm.note = "";
        this.clearActionAttachment();
      });
    },
    async handleActionAttachmentChange(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.actionAttachmentUploading = true;
      try {
        const path = await this.uploadValidatedAttachment(file);
        this.actionForm.attachment = path;
        this.toastOk("Attachment uploaded");
      } catch (e) {
        console.error(e);
        this.toastErr(e.message || "Failed to upload attachment");
        this.clearActionAttachment();
      } finally {
        this.actionAttachmentUploading = false;
      }
    },
    async handleFeeClearanceAttachmentChange(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.feeClearanceAttachmentUploading = true;
      try {
        const path = await this.uploadValidatedAttachment(file);
        this.feeClearanceAttachment = path;
        this.toastOk("Attachment uploaded");
      } catch (e) {
        console.error(e);
        this.toastErr(e.message || "Failed to upload attachment");
        this.clearFeeClearanceAttachment();
      } finally {
        this.feeClearanceAttachmentUploading = false;
      }
    },
    async uploadValidatedAttachment(file) {
      if (file.size > ACTION_ATTACHMENT_MAX_BYTES) {
        throw new Error("File size should not exceed 25 MB");
      }
      const lowerName = String(file.name || "").toLowerCase();
      const hasAllowedExt = ACTION_ATTACHMENT_ALLOWED_EXT.some((ext) =>
        lowerName.endsWith(ext)
      );
      if (
        !ACTION_ATTACHMENT_ALLOWED_TYPES.includes(file.type) &&
        !hasAllowedExt
      ) {
        throw new Error("Only PNG, JPG, DOC, and PDF files are allowed");
      }
      const uploaded = await uploadToMySkool([file]);
      const path = getUploadPath(uploaded[0]);
      if (!path) throw new Error("Invalid upload response");
      return path;
    },
    clearActionAttachmentInput() {
      if (this.$refs.actionAttachmentInput) {
        this.$refs.actionAttachmentInput.value = "";
      }
    },
    clearActionAttachment() {
      this.actionForm.attachment = "";
      this.clearActionAttachmentInput();
    },
    clearFeeClearanceAttachmentInput() {
      if (this.$refs.feeClearanceAttachmentInput) {
        this.$refs.feeClearanceAttachmentInput.value = "";
      }
    },
    clearFeeClearanceAttachment() {
      this.feeClearanceAttachment = "";
      this.clearFeeClearanceAttachmentInput();
    },
    getAttachmentList(attachmentString) {
      if (!attachmentString) return [];
      if (Array.isArray(attachmentString)) {
        return attachmentString.map(String).filter(Boolean);
      }
      return String(attachmentString)
        .split(",")
        .map((url) => url.trim())
        .filter(Boolean);
    },
    isImageFile(url) {
      if (!url) return false;
      const lowerUrl = String(url).toLowerCase();
      return [".jpg", ".jpeg", ".png"].some((ext) => lowerUrl.includes(ext));
    },
    getFileName(url) {
      if (!url) return "Attachment";
      const clean = String(url).split("?")[0];
      const parts = clean.split("/");
      return parts[parts.length - 1] || "Attachment";
    },
    getAttachmentPreviewUrl(url) {
      if (!url) return "";
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, this.isImageFile(url) ? "Image" : "pdf");
    },
    doReassign(toRole) {
      return this.runAction(async () => {
        await this.withdrawalPost(
          "Reassign",
          {
            toRole,
            toUserID: this.withdrawalUserID,
            note: this.reassignNote || `Reassigned to ${toRole}`,
          },
          {
            id: this.withdrawalId,
            userID: this.withdrawalUserID,
            role: "branch_head",
          }
        );
        this.toastOk(`Reassigned to ${this.formatRole(toRole)}`);
        this.reassignNote = "";
      });
    },
    doSetStatus(status) {
      return this.runAction(async () => {
        await this.withdrawalPost(
          "SetStatus",
          {},
          {
            id: this.withdrawalId,
            userID: this.withdrawalUserID,
            role: "branch_head",
            status,
            note: this.statusNote || status,
          }
        );
        this.toastOk(`Status updated: ${status}`);
        this.statusNote = "";
      });
    },
    doSaveFeeClearance() {
      const note = this.feeClearanceNoteTrimmed;
      if (!note) {
        this.toastErr("Enter fee clearance remarks");
        return;
      }
      return this.runAction(async () => {
        await this.withdrawalPost(
          "AddAction",
          {
            actionType: "comment",
            decision: "fee_clearance",
            note,
            attachment: this.feeClearanceAttachment || "",
          },
          {
            id: this.withdrawalId,
            userID: this.withdrawalUserID,
            role: "finance_officer",
          }
        );
        this.toastOk("Fee clearance remarks saved");
        this.clearFeeClearanceAttachment();
      });
    },
    doProceedToHeadOffice(bvModalEvt) {
      const note = this.feeClearanceNoteTrimmed;
      if (!note) {
        if (bvModalEvt && typeof bvModalEvt.preventDefault === "function") {
          bvModalEvt.preventDefault();
        }
        this.toastErr(
          "Enter fee clearance remarks before proceeding to Head Office"
        );
        return;
      }
      return this.runAction(async () => {
        await this.withdrawalPost(
          "ProceedToHeadOffice",
          {
            feeClearanceNote: note,
            attachment: this.feeClearanceAttachment || "",
          },
          {
            id: this.withdrawalId,
            userID: this.withdrawalUserID,
            role: "finance_officer",
          }
        );
        this.toastOk("Proceeded to Head Office");
        this.confirmProceedHeadOffice = false;
        this.clearFeeClearanceAttachment();
      });
    },
    doFinalize(bvModalEvt) {
      const note = this.feeClearanceNoteTrimmed;
      if (!note) {
        if (bvModalEvt && typeof bvModalEvt.preventDefault === "function") {
          bvModalEvt.preventDefault();
        }
        this.toastErr("Enter fee clearance remarks before marking left");
        this.confirmFinalize = true;
        return;
      }
      return this.runAction(async () => {
        const finalizeRole = this.hasWithdrawalRole("head_office")
          ? "head_office"
          : "finance_officer";
        await this.withdrawalPost(
          "FinalizeLeft",
          {
            feeClearanceNote: note,
          },
          {
            id: this.withdrawalId,
            userID: this.withdrawalUserID,
            role: finalizeRole,
          }
        );
        this.toastOk("Student marked Left. Retention history saved.");
        this.confirmFinalize = false;
      });
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>

<style lang="scss" scoped>
@import "./withdrawalPanelStyles.scss";

.sw-profile-banner {
  background: linear-gradient(135deg, #f3f0ff 0%, #faf9ff 55%, #ffffff 100%);
  border: 1px solid #e8e2ff;
  border-radius: 16px;
  padding: 1rem 1.15rem;
}

.sw-profile-avatar {
  border: 3px solid #fff;
  box-shadow: 0 6px 16px rgba(115, 103, 240, 0.18);
}

.sw-profile-name {
  color: #2d1b69;
  font-weight: 800;
  letter-spacing: -0.3px;
  font-size: 1.35rem;
  line-height: 1.25;
}

.sw-profile-fn {
  color: #7c5dfa;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  margin: 0;
}

.sw-info-pill {
  display: inline-flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e8e2ff;
  border-radius: 999px;
  padding: 0.3rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #4b5563;
}

.sw-detail-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}

.sw-detail-cell {
  background: #fff;
  border: 1px solid #ece7ff;
  border-radius: 12px;
  padding: 0.75rem 0.9rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #d4c7ff;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.08);
  }

  &--span2 {
    grid-column: span 2;
  }

  &--full {
    grid-column: 1 / -1;
  }
}

.sw-detail-label {
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #82868b;
  margin-bottom: 0.3rem;
}

.sw-detail-value {
  display: block;
  color: #2d1b69;
  font-weight: 700;
  font-size: 0.95rem;
  line-height: 1.35;
  word-break: break-word;
}

@media (max-width: 992px) {
  .sw-detail-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 576px) {
  .sw-detail-grid {
    grid-template-columns: 1fr;
  }

  .sw-detail-cell--span2 {
    grid-column: auto;
  }
}

.sw-empty-actions {
  text-align: center;
  padding: 1.5rem 1rem;
  border: 1px dashed #e0d8ff;
  border-radius: 14px;
  background: #faf9ff;
}

.sw-action-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;

  &--single {
    grid-template-columns: 1fr;
    max-width: 520px;
  }
}

.sw-action-panel {
  background: #fff;
  border: 1px solid #ece7ff;
  border-radius: 16px;
  padding: 1rem 1.05rem;
  box-shadow: 0 2px 10px rgba(115, 103, 240, 0.05);
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #d4c7ff;
    box-shadow: 0 6px 18px rgba(115, 103, 240, 0.1);
  }

  &--submit {
    background: linear-gradient(180deg, #ffffff 0%, #f8f6ff 100%);
    border-color: #ddd4ff;
  }

  &--danger {
    border-color: #ffd0d0;
    background: linear-gradient(180deg, #fff 0%, #fff8f8 100%);
  }

  &__head {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  &__title {
    color: #2d1b69;
    font-weight: 700;
    font-size: 0.95rem;
    line-height: 1.3;
  }

  &__sub {
    color: #82868b;
    font-size: 0.78rem;
    line-height: 1.35;
    margin-top: 0.1rem;
  }
}

.sw-action-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--primary {
    background: #f0edff;
    color: #7367f0;
  }
  &--success {
    background: #e8f8ef;
    color: #28c76f;
  }
  &--info {
    background: #e6f7fb;
    color: #00cfe8;
  }
  &--warning {
    background: #fff3e0;
    color: #ff9f43;
  }
  &--danger {
    background: #ffeaea;
    color: #ea5455;
  }
}

.sw-fee-history-btn {
  white-space: nowrap;
}

.sw-fee-history-table-wrap {
  max-height: min(60vh, 520px);
  overflow: auto;
  border: 1px solid #ebe9f1;
  border-radius: 10px;
}

.sw-fee-history-table {
  th,
  td {
    vertical-align: middle;
    white-space: nowrap;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f8f9fa;
  }
}

.sw-fee-history-pagination {
  ::v-deep .page-link {
    padding: 0.25rem 0.55rem;
  }
}

.sw-action-input {
  border-radius: 10px !important;
  border-color: #e0d8ff !important;
  resize: vertical;
  min-height: 72px;

  &:focus {
    border-color: #7367f0 !important;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12) !important;
  }
}

.sw-action-btn {
  width: 100%;
  border-radius: 10px !important;
  font-weight: 600 !important;
  min-height: 40px;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.18);

  &--primary {
    background: linear-gradient(135deg, #7367f0 0%, #9d7aff 100%) !important;
    border: none !important;
  }
}

.sw-action-btn-sm {
  border-radius: 10px !important;
  font-weight: 600 !important;
  min-height: 36px;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.sw-status-btns {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;

  &--two {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.sw-status-btn {
  border-radius: 10px !important;
  font-weight: 600 !important;
  min-height: 38px;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  border: none !important;
  color: #fff !important;

  &--hold {
    background: #ff9f43 !important;
    box-shadow: 0 4px 12px rgba(255, 159, 67, 0.28);
  }
  &--reject {
    background: #ea5455 !important;
    box-shadow: 0 4px 12px rgba(234, 84, 85, 0.28);
  }
  &--resume {
    background: #fff !important;
    color: #28c76f !important;
    border: 1.5px solid #28c76f !important;
    box-shadow: none;
  }
}

@media (max-width: 992px) {
  .sw-action-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .sw-status-btns {
    grid-template-columns: 1fr;
  }

  .sw-history-item .d-flex.justify-content-between {
    flex-direction: column;
    align-items: stretch !important;
  }

  .sw-history-when {
    align-self: flex-start;
    margin-top: 0.35rem;
  }
}

.sw-history-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.sw-history-item {
  display: flex;
  gap: 0.75rem;
  padding: 0.85rem 0.95rem;
  background: #fff;
  border: 1px solid #e4deff;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.06);
}

.sw-history-avatar {
  flex-shrink: 0;
  width: 40px;
  display: flex;
  justify-content: center;
  padding-top: 0.1rem;
}

.sw-timeline-dot {
  width: 14px;
  height: 14px;
  margin-top: 0.35rem;
  border-radius: 50%;
  background: #7c5dfa;
  border: 3px solid #efe8ff;
  box-shadow: 0 0 0 2px #cbb9ff;
}

.sw-history-body {
  flex: 1;
  min-width: 0;
}

.sw-history-when {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  flex-shrink: 0;
  padding: 0.4rem 0.7rem 0.4rem 0.45rem;
  border-radius: 12px;
  background: linear-gradient(135deg, #faf9ff 0%, #f0edff 100%);
  border: 1px solid #ddd4ff;
  box-shadow: 0 2px 6px rgba(115, 103, 240, 0.08);
}

.sw-history-when__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #fff;
  color: #7367f0;
  flex-shrink: 0;
}

.sw-history-when__text {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.15;
  min-width: 0;
}

.sw-history-when__date {
  color: #2d1b69;
  font-weight: 700;
  font-size: 0.8rem;
  white-space: nowrap;
}

.sw-history-when__time {
  color: #7367f0;
  font-weight: 600;
  font-size: 0.74rem;
  white-space: nowrap;
  margin-top: 0.1rem;
}

.sw-history-title {
  color: #2d1b69;
  font-weight: 700;
  font-size: 0.95rem;
  margin-bottom: 0.2rem;
}

.sw-history-actor {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  color: #6e6b7b;
  font-size: 0.8rem;
}

.sw-history-role {
  color: #7367f0;
  font-weight: 600;
  margin-left: 0.15rem;
}

.sw-history-note {
  margin-top: 0.45rem;
  padding: 0.55rem 0.7rem;
  background: #f8f7ff;
  border-radius: 10px;
  color: #4a4568;
  font-size: 0.875rem;
  line-height: 1.45;
  white-space: pre-wrap;
}

.sw-history-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.55rem;
}

.sw-history-attachment-item {
  display: inline-flex;
}

.sw-history-attachment-image-link {
  display: inline-block;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e0d8ff;
  line-height: 0;
}

.sw-history-attachment-image {
  display: block;
  width: 88px;
  height: 88px;
  object-fit: cover;
}

.sw-history-attachment-file {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: #f0edff;
  color: #7367f0;
  font-size: 0.78rem;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    background: #e4ddff;
    color: #5e50ee;
    text-decoration: none;
  }
}

.sw-file-picker__field {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 40px;
  padding: 0.35rem 0.45rem 0.35rem 0.75rem;
  border: 1.5px solid #e0d8ff;
  border-radius: 10px;
  background: #fff;
}

.sw-file-picker__name {
  flex: 1;
  min-width: 0;
  font-size: 0.82rem;
  color: #2d1b69;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sw-file-picker__btn {
  flex-shrink: 0;
}

.sw-action-attachment-preview {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.55rem;
  border-radius: 10px;
  background: #f8f7ff;
  border: 1px solid #e0d8ff;
}

.sw-action-attachment-link {
  flex: 1;
  min-width: 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: #7367f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;

  &:hover {
    color: #5e50ee;
    text-decoration: underline;
  }
}

.sw-action-attachment-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #fff;
  color: #ea5455;
  cursor: pointer;
}
</style>
